import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import PensumService from '../../services/pensum.service';
import CursoService from '../../services/curso.service';
import GradeService from '../../services/grade.service';
import TransferList from './TransferList';

const PensumForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    grade_id: '',
  });
  const [selectedCursos, setSelectedCursos] = useState([]);
  const [allCursos, setAllCursos] = useState([]);
  const [grades, setGrades] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { gradeId: gradeIdParam } = useParams();
  const isEdit = Boolean(gradeIdParam);
  const gradeId = gradeIdParam ? parseInt(gradeIdParam, 10) : null;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.grade_id) {
      loadPensumsForGrade(formData.grade_id);
    }
  }, [formData.grade_id]);

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      const [cursosRes, gradesRes] = await Promise.all([
        CursoService.getAll(),
        GradeService.getAll(),
      ]);
      setAllCursos(cursosRes.data?.data || []);
      setGrades(gradesRes.data?.data || []);

      if (isEdit && gradeId) {
        setFormData({
          grade_id: gradeId.toString(),
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoadingData(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'grade_id' && value) {
      // Load existing pensums for this grade
      loadPensumsForGrade(value);
    } else if (name === 'grade_id' && !value) {
      setSelectedCursos([]);
    }
  };

  const loadPensumsForGrade = async (gradeId) => {
    try {
      const response = await PensumService.getByGrade(gradeId);
      const pensums = response.data?.data || [];
      const cursoIds = pensums.map(p => p.curso_id);
      const cursos = allCursos.filter(c => cursoIds.includes(c.id));
      // Sort by orden from pensums
      const sortedCursos = cursoIds.map(id => cursos.find(c => c.id === id)).filter(Boolean);
      setSelectedCursos(sortedCursos);
    } catch (error) {
      console.error('Error loading pensums for grade:', error);
      setSelectedCursos([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.grade_id) {
      showSnackbar('Please select a grade', 'error');
      return;
    }

    if (selectedCursos.length === 0) {
      showSnackbar('Please select at least one curso', 'error');
      return;
    }

    try {
      setLoading(true);
      const data = {
        grade_id: formData.grade_id,
        curso_ids: selectedCursos.map(c => c.id),
      };

      await PensumService.create(data);
      showSnackbar(isEdit ? 'Pensum updated successfully' : 'Pensum created successfully');
      navigate('/pensums');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            `Error creating pensum`);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/pensums')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {isEdit ? t('pensums.editPensumForGrade') : t('pensums.newPensum')}
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {t('pensums.savePensum')}
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('pensums.grade')}</InputLabel>
                <Select
                  value={formData.grade_id}
                  name="grade_id"
                  label={t('pensums.grade')}
                  onChange={handleInputChange}
                >
                  {grades.map((grade) => (
                    <MenuItem key={grade.id} value={String(grade.id)}>
                      {grade.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {formData.grade_id && (
        <TransferList
          availableItems={allCursos}
          selectedItems={selectedCursos}
          onSelectionChange={setSelectedCursos}
          title={t('pensums.assignCursosToGrade')}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PensumForm;
